import { Link } from 'react-router-dom'

const guide = {
  slug: 'porn-blocker-without-accountability-partner',
  category: 'holds',
  kicker: 'Privacy',
  title: 'A porn blocker without an accountability partner — nobody has to know',
  seoTitle: 'Porn Blocker Without an Accountability Partner',
  description: `Most serious blockers make you name someone who gets your reports. If you would rather quit without telling anyone: a lock instead of a witness, no history kept.`,
  published: '2026-08-25',
  minutes: 6,
  intro: `Open the sign-up flow of most serious porn blockers and you hit the
          same screen: "choose your accountability partner". Someone who gets
          your reports, your screenshots, a weekly email. For some people
          that works. For a lot of people it's the reason they close the
          tab. This is for the second group.`,
  next: ['how-to-quit-porn', 'porn-blocker-you-cant-uninstall', 'covenant-eyes-alternative'],
  body: (
    <>
      <h2>Why the partner model exists</h2>
      <p>
        Accountability software has one theory: you won't stop because
        you're not watched, so it makes someone watch. Your activity is
        analysed, sometimes screenshotted, and a person you named receives
        a report. The block, where there is one, is secondary; the witness
        is the product.
      </p>
      <p>
        It's a coherent theory and it helps some people. It also has costs
        that nobody advertises.
      </p>

      <h2>What it costs</h2>
      <ul>
        <li><b>You have to tell someone.</b> A partner, a friend, a brother, a pastor. For many people, the shame of that conversation is heavier than the habit, so the conversation never happens and neither does the install.</li>
        <li><b>The relationship becomes the mechanism.</b> A slip is no longer private; it's a notification on someone else's phone. Some relationships carry that. Many don't.</li>
        <li><b>The witness can be talked to.</b> "Can you approve the uninstall, I need it for work." One message, on a night you'll send it. A person is a lock with a conversation as the key.</li>
        <li><b>Something reads your activity.</b> That's the deal: reports need data. Your browsing, on a server, tagged with your name.</li>
      </ul>

      <h2>The other model: a lock instead of a witness</h2>
      <p>
        There's a different theory. You don't relapse because nobody's
        watching; you relapse because at two in the morning the door is
        open and you're not the person who shut it. Remove the door and
        there's nothing to witness.
      </p>
      <p>
        That's the SELFCNTRL model, and it has no partner in it:
      </p>
      <ul>
        <li><b>The block is technical, not social.</b> A DNS-level category filter on every device — every app, every browser, private mode included. On iPhone and iPad it sits on a supervised profile that can't be removed from the phone and forbids adding a VPN. Nobody has to hold the key, because there isn't one to hold: taking it off is a deliberate request from your account with a cooling-off delay.</li>
        <li><b>It only tightens.</b> Adding a locked day applies at once; loosening waits twenty-four hours; the floor never drops. The delay does what a partner was supposed to do — put a night between the urge and the act — without a person in the middle.</li>
        <li><b>Nobody is told.</b> No partner, no email to a third party, no shared dashboard.</li>
        <li><b>Nothing is read.</b> Filtering works on categories, not on a list of addresses. We don't read your history, we store nothing, and no report goes to anyone — there isn't one to send.</li>
        <li><b>Nothing on the statement.</b> Billing is discreet; nothing explicit appears on your bank statement.</li>
      </ul>

      <h2>What "nobody knows" includes</h2>
      <p>
        Supervision sounds like surveillance. It isn't. It's used for one
        thing — keeping the filter profile on. Not your photos, messages or
        passwords. No remote commands, no wipe, no location, no app
        installed or removed for you. It's written out in the
        <Link to="/confidentialite"> privacy policy</Link> with the four
        processors we actually use, and it stays between you and the door.
      </p>

      <h2>If you do want someone in the loop</h2>
      <p>
        Nothing stops you from telling a person. You just don't have to, and
        the block doesn't depend on it. The lock holds the same whether one
        person knows or nobody does.
      </p>

      <h2>Ten minutes, once</h2>
      <ol>
        <li><Link to="/register">Create your space</Link>. One email address. Nobody but you knows why you're here.</li>
        <li>Lock the phone: cable, supervisor, QR code. <Link to="/guides/how-to-block-porn-on-iphone">The iPhone guide</Link> has every step.</li>
        <li>Pick your pace and a night curfew. Then there's no fight left, and no one to report it to.</li>
      </ol>
    </>
  ),
  faq: [
    {
      q: 'Is there a porn blocker that doesn’t need an accountability partner?',
      a: `Yes. SELFCNTRL holds through a supervised, non-removable profile
          and a cooling-off delay instead of a witness. There is no partner
          screen, no report and nothing to share.`
    },
    {
      q: 'Does anyone at SELFCNTRL see my activity?',
      a: `No. The filter works on categories at DNS level; it doesn't keep a
          history, so there's nothing for anyone to see. Nothing is stored,
          nothing is reported.`
    },
    {
      q: 'What shows on my bank statement?',
      a: `A discreet descriptor. Nothing explicit.`
    }
  ]
}

export default guide
